import { useContext, useState } from 'react';
import jwt from 'jwt-decode';
import { Store } from '../store/Store';
import { useParams,useSearchParams } from 'react-router-dom';

import lodash from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import B2CClientConnector from '../interfaces/B2CClientConnector';
import { randomFromString, blend } from '../utils/secureColors';
import {
  getLatestTimestamp,
  isRefreshing,
  getStatusTextFromLog,
  truncateText,
  isTruncated,
} from '../utils/clientConnectorUtils';
import StatusIcon from '../components/StatusIcon';


dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(timezone);

const REFRESH_DETAIL_MAX_LENGTH = 50;
//although this is the Business2CloudConnectorConfigureCad.tsx const is still called Business2CloudConnectorManageCard

const Business2CloudConnectorManageCard = ({
  clientConnector,
  userList,
  onAssignUserClick,
  onEditClientConnectorClick,
  onDisconnectClientConnectorClick,
  onDeleteClientConnectorClick,
  onProcessClientConnectorClick,
  onTrialBalanceClick,
}: {
  clientConnector: B2CClientConnector;
  userList: any[] | undefined;
  onAssignUserClick: (clientConnector: B2CClientConnector) => void;
  onEditClientConnectorClick: (clientConnector: B2CClientConnector) => void;
  onDisconnectClientConnectorClick: (clientConnector: B2CClientConnector) => Promise<any>;
  onDeleteClientConnectorClick: (clientConnector: B2CClientConnector) => Promise<any>;
  onProcessClientConnectorClick: (clientConnector: B2CClientConnector) => Promise<void>;
  onTrialBalanceClick: (clientConnector: B2CClientConnector) => Promise<void>;
}) => {

  

  const users =
    clientConnector.Properties.find((_: any) => _.Name === 'portal.user')
      ?.Value?.trim()
      .split(' ')
      .map((user: any) => userList?.find((_) => _.Id === user)) || [];

  const { connectorType } = useParams();
  const [searchParams] = useSearchParams();
  const store = useContext(Store).store;
  const currentRole = jwt<any>(store?.jwt)?.Role?.toLowerCase() ?? '';
  const search = searchParams.get('search') as string;
  const [showCopied, setShowCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const rawDetail = clientConnector.Log?.RefreshDetail || 'No detail';
  const truncatedDetail = truncateText(rawDetail, REFRESH_DETAIL_MAX_LENGTH);
  const truncated = isTruncated(rawDetail, REFRESH_DETAIL_MAX_LENGTH);

  // Copy to clipboard
  const onCopyRefreshDetailToClipboard = () => {
    if (clientConnector.Log?.RefreshDetail) {
      navigator.clipboard.writeText(clientConnector.Log.RefreshDetail);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 3000);
    }
  };

  const filter =
    !search ||
    clientConnector.Name.toLowerCase().indexOf(search) >= 0 ||
    users.filter(
      (user: any) =>
        !search ||
        user?.FirstName?.toLowerCase().indexOf(search) >= 0 ||
        user?.LastName?.toLowerCase().indexOf(search) >= 0 ||
        user?.EmailAddress?.toLowerCase().indexOf(search) >= 0
    ).length > 0;

  if (!filter) return <></>;

  const latestTimestamp = getLatestTimestamp(clientConnector.Log);

const refreshing = isRefreshing(clientConnector.Log);


  const renderStatusText = () => {
    if (clientConnector.Status === 'DISCONNECTED' || clientConnector.Status === 'DELETED') return <></>;

    if (clientConnector.Log?.Forbidden) {
      return (
        <div className="d-flex flex-column">
          <small className="text-warning">
            <b>This connector may have been disconnected. Please re-authorise or disconnect fully.</b>
          </small>
          {latestTimestamp?.value && (
            <small className="text-muted">
            {/* {latestTimestamp.label} <b>{dayjs(latestTimestamp.value).fromNow()}</b> */}
            {getStatusTextFromLog(clientConnector.Log)} <b>{dayjs(latestTimestamp?.value).fromNow()}</b>

            </small>
          )}
        </div>
      );
    }

    if (!latestTimestamp?.value)
      return <small className="text-muted">Never started</small>;

    return (
      <small className="text-muted">
        {getStatusTextFromLog(clientConnector.Log)} <b>{dayjs(latestTimestamp?.value).fromNow()}</b>
      </small>
    );
  };

  const renderBody = () => {
    if (
      clientConnector.Status === 'DISCONNECTED' &&
      clientConnector.Log?.RefreshDetail === 'Successfully refreshed.'
    ) {
      return (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <img src="/img/sync.svg" alt="Sync" className="sync-icon" />
        </div>
      );
    }
    if (clientConnector.Status === 'DISCONNECTED') {
      if (refreshing) {
        return (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <img src="/img/sync.svg" alt="Sync" className="sync-icon" />
          </div>
        );
      }
      return (
        <div>
         {/* <small> */}
          {clientConnector.Log && (
            <span>
              {!refreshing && (
                <>
                  <br />
                  <b>Refresh detail:</b>
                  <div className="d-flex align-items-center">
                    <small
                      className="text-truncate"
                      style={{
                        maxWidth: '90%',
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={rawDetail}
                    >
                      {truncatedDetail}
                    </small>

                    {/* If truncated, show '... more' link */}
                    {truncated && (
                      <span
                        className="ms-1 text-primary"
                        style={{ cursor: 'pointer', fontSize: '0.9em' }}
                        onClick={() => setShowModal(true)}
                      >
                        more
                      </span>
                    )}

                    {/* Copy button if there's a real detail */}
                    {clientConnector.Log?.RefreshDetail && (
                      <button
                        className="ms-2 btn btn-outline-secondary btn-xs btn-round"
                        onClick={onCopyRefreshDetailToClipboard}
                        title="Copy full message"
                      >
                        {showCopied ? (
                          <span className="material-symbols-rounded">check</span>
                        ) : (
                          <span className="material-symbols-rounded">content_copy</span>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </span>
          )}
        {/* </small> */}
        </div>
      );
    }

    if (!users.length) return '';

    return (
      <div className="status-label-stack">
        {lodash
          .take(users, 10)
          .filter((_: any) => _)
          .map((user: any, index: number) => {
            const baseColor = randomFromString(user.FirstName + user.LastName);
            const backgroundColor = blend(baseColor, '#ffffff', 0.9);
            return (
              <span
                className="status-label"
                key={index}
                style={{ backgroundColor }}
                title={`${user.FirstName} ${user.LastName}`}
              >
                {user.FirstName.charAt(0)}
                {user.LastName?.charAt(0)}
              </span>
            );
          })}
        {users.length > 10 && <span className="status-label additional">+ {users.length - 10}</span>}
      </div>
    );
  };

  // Determine allocation/role gating for Trial Balance
  const isXero = (connectorType || '').toLowerCase() === 'xero';
  const isAdminRole = ['admin', 'superadmin', 'orgadmin'].some(r => currentRole.includes(r));
  const isAllocatedToUsers = !!clientConnector?.Properties?.find((_: any) => _.Name === 'portal.user' && _.Value?.trim());
  const canRunTrialBalance = isXero && (isAdminRole || isAllocatedToUsers);

  return (
    <div
      className={`tw-shadow tw-transition-all hover:tw-shadow-lg hover:tw-ring tw-ring-offset-1 tw-ring-blue-500 tw-no-underline tw-font-semibold !tw-text-slate-900 card card-sm ${
        clientConnector.Status === 'DISCONNECTED' || clientConnector.Status === 'DELETED'
          ? 'disabled'
          : ''
      }`}
      style={{ height: 230 }}
    >
      <div className="card-header align-items-start">
        <div style={{ maxWidth: '80%' }}>
          <div className="d-flex flex-column gap-3">
            <span className="text-uppercase fw-bold">
              {(clientConnector.Status === 'DELETED' && (
                <span
                  className="p-1 px-2 fs-11 text-danger bg-danger bg-opacity-10 rounded-1 d-inline"
                  style={{ marginLeft: '-0.2rem' }}
                >
                  deleted
                </span>
              )) ||
                (clientConnector.Status === 'DISCONNECTED' && (
                  <span
                    className="p-1 px-2 fs-11 text-warning bg-warning bg-opacity-10 rounded-1 d-inline"
                    style={{ marginLeft: '-0.2rem' }}
                  >
                    disconnected
                  </span>
                )) ||
                (clientConnector.Status !== 'DISCONNECTED' && (
                  <span
                    className="p-1 px-2 fs-11 text-success bg-success bg-opacity-10 rounded-1 d-inline"
                    style={{ marginLeft: '-0.2rem' }}
                  >
                    connected
                  </span>
                ))}
            </span>
            <span className="d-flex flex-column">
              {clientConnector.Name !== clientConnector.OriginalName && (
                <small className="fw-bold darker-text fs-11">{clientConnector.OriginalName}</small>
              )}
              <span
                style={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: 'inline-block',
                }}
              >
                {clientConnector.Name}
              </span>
              {renderStatusText()}
            </span>
          </div>
        </div>
        
       <StatusIcon log={clientConnector.Log} status={clientConnector.Status} />

      </div>
      <div className="card-body">{renderBody()}</div>
      <div className="card-footer gap-2 mt-auto">
        <div className="d-flex flex-row gap-2 align-items-center">
          {clientConnector.Status !== 'DISCONNECTED' && clientConnector.Status !== 'DELETED' && (
            <>
              <button className="btn btn-sm btn-primary" onClick={() => onAssignUserClick(clientConnector)}>
                Manage users
              </button>
              {canRunTrialBalance && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  title="Trial Balance"
                  onClick={(e) => {
                    const btn = e.currentTarget as HTMLButtonElement | null;
                    if (btn) btn.blur();
                    onTrialBalanceClick(clientConnector);
                  }}
                  onMouseUp={(e) => (e.currentTarget as HTMLButtonElement).blur()}
                  onMouseLeave={(e) => (e.currentTarget as HTMLButtonElement).blur()}
                >
                  Trial Balance
                </button>
              )}
            </>
          )}
          {clientConnector.Status !== 'DELETED' && store.user.roleLevel <= 2 && (
            <div className="d-flex flex-row gap-2 flex-grow ms-auto">
              <button
                className="btn btn-sm btn-round btn-secondary"
                title="Edit"
                onClick={() => onEditClientConnectorClick(clientConnector)}
              >
                <span className="material-symbols-rounded">edit</span>
              </button>
              <button
                className="btn btn-sm btn-round btn-danger"
                title="Delete"
                onClick={() => onDeleteClientConnectorClick(clientConnector)}
              >
                <span className="material-symbols-rounded">delete</span>
              </button>
              {clientConnector.Status !== 'DISCONNECTED' && (
                <button
                  className="btn btn-sm btn-round btn-outline-danger"
                  title="Disconnect"
                  onClick={() => onDisconnectClientConnectorClick(clientConnector)}
                >
                  <span className="material-symbols-rounded">link_off</span>
                </button>
              )}
              {connectorType === 'xero' && clientConnector.Log?.Forbidden && (
                <button
                  className="btn btn-sm btn-round btn-primary"
                  title="Retry"
                  onClick={() => onProcessClientConnectorClick(clientConnector)}
                >
                  <span className="material-symbols-rounded">refresh</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {showModal && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog" style={{ marginTop: '10vh' }}>
            <div className="modal-content">
              <div
                className="modal-header d-flex align-items-center justify-content-between"
                style={{ backgroundColor: '#0e7bbb' }}
              >
                <h5 className="modal-title mb-0" style={{ color: '#fff' }}>
                  Full Refresh Detail
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={onCopyRefreshDetailToClipboard}
                  >
                    {showCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '1rem' }}>{rawDetail}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Business2CloudConnectorManageCard;
